import { useMemo } from "react";

import { RVSideNavLayout, type PageSubNavItem } from "@regal-voice/ui";
import { usePageAvailability } from "App/routes/Hooks/usePageAvilability/usePageAvailability";
import { TOP_LEVEL_PAGES_TITLE_MAP } from "App/routes/pageNames";
import { AppPagePaths } from "App/routes/paths";
import { CollapsiblePanel } from "Utils/CollapsiblePanels";
import { useKnownPanels } from "Utils/CollapsiblePanels/knownPanels";

import { ORCHESTRATION_SUB_NAV_TITLES, ORCHESTRATION_SUB_PAGE_TITLES_PATCH } from "../../orchestrationMetaData";
import { CampaignsPaths } from "../../Pages/campaigns/paths";
import { CustomFunctionPaths } from "../../Pages/customFunction/paths";
import { IVRPaths } from "../../Pages/ivrs/paths";
import { orchestrationPathResources } from "../../Pages/orchestrationPaths";
import { RoutingRulesPaths } from "../../Pages/taskRouting/Pages/paths";
import { queuesPaths } from "../../Pages/taskRouting/Pages/QueuesListPage/Pages/queuesPaths";

export function OrchestrationSubNav(): JSX.Element {
    const { orchestrationPages } = usePageAvailability();
    const { showJourneys, showCampaigns, showIVR, showTaskRouting, showCustomFunctions } = orchestrationPages;

    const items: PageSubNavItem[] = useMemo(() => {
        const navItems: PageSubNavItem[] = [];

        if (showJourneys) {
            navItems.push({
                key: `${orchestrationPathResources.journeys}`,
                title: ORCHESTRATION_SUB_NAV_TITLES[orchestrationPathResources.journeys],
                href: `/${orchestrationPathResources.journeys}`,
            });
        }

        if (showCampaigns) {
            navItems.push({
                key: `${orchestrationPathResources.campaigns}`,
                title: ORCHESTRATION_SUB_NAV_TITLES[orchestrationPathResources.campaigns],
                href: `/${orchestrationPathResources.campaigns}`,
                tabs: [
                    `/${orchestrationPathResources.campaigns}/${CampaignsPaths.edit}`,
                    `/${orchestrationPathResources.campaigns}/${CampaignsPaths.editDashboard}`,
                ],
            });
        }

        if (showIVR) {
            navItems.push({
                key: `${orchestrationPathResources.ivrs}`,
                title: ORCHESTRATION_SUB_PAGE_TITLES_PATCH[orchestrationPathResources.ivrs],
                href: `/${orchestrationPathResources.ivrs}`,
                tabs: [`/${orchestrationPathResources.ivrs}/${IVRPaths.ivrDetail}`],
            });
        }

        if (showCustomFunctions) {
            navItems.push({
                key: `${orchestrationPathResources.customFunctions}`,
                title: ORCHESTRATION_SUB_NAV_TITLES[orchestrationPathResources.customFunctions],
                href: `/${orchestrationPathResources.customFunctions}`,
                tabs: [`/${orchestrationPathResources.customFunctions}/${CustomFunctionPaths.functionDetail}`],
            });
        }

        if (showTaskRouting) {
            navItems.push({
                key: `${orchestrationPathResources.routing}`,
                title: ORCHESTRATION_SUB_NAV_TITLES[orchestrationPathResources.routing],
                href: `/${orchestrationPathResources.routing}`,
                children: [
                    {
                        key: `${orchestrationPathResources.routing}/${RoutingRulesPaths.rules}`,
                        title: ORCHESTRATION_SUB_NAV_TITLES[RoutingRulesPaths.rules],
                        href: `/${orchestrationPathResources.routing}/${RoutingRulesPaths.rules}`,
                        tabs: [
                            `/${orchestrationPathResources.routing}/${RoutingRulesPaths.ruleByType}`,
                            `/${orchestrationPathResources.routing}/${RoutingRulesPaths.ruleByTypeVersion}`,
                        ],
                    },
                    {
                        key: `${orchestrationPathResources.routing}/${RoutingRulesPaths.queues}`,
                        title: ORCHESTRATION_SUB_PAGE_TITLES_PATCH[
                            `${orchestrationPathResources.routing}/${RoutingRulesPaths.queues}`
                        ],
                        href: `/${orchestrationPathResources.routing}/${RoutingRulesPaths.queues}`,
                        tabs: [
                            `/${orchestrationPathResources.routing}/${RoutingRulesPaths.queues}/${queuesPaths.edit}`,
                        ],
                    },
                    {
                        key: `${orchestrationPathResources.routing}/${RoutingRulesPaths.attributes}`,
                        title: ORCHESTRATION_SUB_NAV_TITLES[RoutingRulesPaths.attributes],
                        href: `/${orchestrationPathResources.routing}/${RoutingRulesPaths.attributes}`,
                    },
                ],
            });
        }

        return navItems;
    }, [showJourneys, showCampaigns, showIVR, showTaskRouting, showCustomFunctions]);

    const { orchestration } = useKnownPanels();

    return (
        <CollapsiblePanel panelName={orchestration.name} expandedWidth={orchestration.width}>
            <RVSideNavLayout
                sidenavTitle={TOP_LEVEL_PAGES_TITLE_MAP[AppPagePaths.orchestration]}
                items={items}
                basePath={AppPagePaths.orchestration}
                hasSubPages
                defaultOpenKeys={[`${orchestrationPathResources.routing}`]}
            />
        </CollapsiblePanel>
    );
}
